import { models, Report, Embed } from 'powerbi-client';
import {
  MonthNumToDisplayName,
  Page,
  PageDisplayNameMap,
  ValidMonthlyRepricingMonth,
  ValidMonthlyRepricingYear,
} from './types';

export function isReport(embed: Embed): embed is Report {
  return embed.embedtype === 'report';
}

export const caseStatusPages = {
  accessIssueActive: 'Access Issue Active',
  accessIssueClosed: 'Access Issue Closed',
  balanceBillActive: 'Balance Bill Active',
  balanceBillClosed: 'Balance Bill Closed',
};

export const monthlyReportingPage = {
  totalsRecap: 'Totals Recap',
  AdditionalMonthlyPayments: 'Additional Monthly Payments',
  activeCases: 'Active Cases',
  closedCases: 'Closed Cases',
  claimsByMonth: 'Claims by Month',
};

export const planHealthReportingPage = {
  coverPage: 'Cover Page',
  ExecutiveSummary: 'Executive Summary',
  HighCostDrivers: 'High-Cost Drivers',
  claimSummaryAllTime: 'Claim Summary - All Time',
  claimSummaryPlanYear: 'Claim Summary - Plan Year',
  utilizationAllTime: 'Utilization - All Time',
  utilizationPlanYear: 'Utilization - Plan Year',
  balanceBillSummaryAllTime: 'Balance Bill Summary - All Time',
  balanceBillSummaryPlanYear: 'Balance Bill Summary - Plan Year',
  accessIssueSummaryAllTime: 'Access Issue Summary - All Time',
  accessIssueSummaryPlanYear: 'Access Issue Summary - Plan Year',
  caseDetails: 'Case Details',
  claimDetails: 'Claim Details',
};

export const providerAcceptanceReportingPage = {
  stateSummary: 'State Summary',
  providerSummary: 'Provider Summary',
};

const firstPublishedYear = 2019;

export function determineReportYears(
  lastPublishedYear?: ValidMonthlyRepricingYear
): ValidMonthlyRepricingYear[] {
  if (!lastPublishedYear) {
    return [];
  }

  const reportYears: ValidMonthlyRepricingYear[] = [];
  for (let y = lastPublishedYear; y >= firstPublishedYear; y--) {
    reportYears.push(y);
  }
  return reportYears;
}

export function monthsToInclude(limit?: ValidMonthlyRepricingMonth) {
  return (month: MonthNumToDisplayName): boolean => {
    return limit ? month[0] <= limit : true;
  };
}

export const months: MonthNumToDisplayName[] = [
  [1, 'January'],
  [2, 'February'],
  [3, 'March'],
  [4, 'April'],
  [5, 'May'],
  [6, 'June'],
  [7, 'July'],
  [8, 'August'],
  [9, 'September'],
  [10, 'October'],
  [11, 'November'],
  [12, 'December'],
];

export function setMonthlyRepricingFilters(
  year?: ValidMonthlyRepricingYear,
  month?: ValidMonthlyRepricingMonth
): models.IBasicFilter[] {
  const filters = [];
  if (year !== undefined) {
    filters.push(createYearFilter(year));
  }
  if (month !== undefined) {
    filters.push(createMonthFilter(month));
  }
  return filters;
}

export function createYearFilter(year: ValidMonthlyRepricingYear): models.IBasicFilter {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table: 'public reportmonthlyrepricingfinal',
      column: 'reportyear',
    },
    operator: 'In',
    values: [year],
    filterType: models.FilterType.Basic,
    requireSingleSelection: true,
    displaySettings: {
      isLockedInViewMode: true,
      displayName: 'Report Year',
    },
  };
}

export function createMonthFilter(month: ValidMonthlyRepricingMonth): models.IBasicFilter {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table: 'public reportmonthlyrepricingfinal',
      column: 'reportmonth',
    },
    operator: 'In',
    values: [month],
    filterType: models.FilterType.Basic,
    requireSingleSelection: true,
    displaySettings: {
      isLockedInViewMode: true,
      displayName: 'Report Month',
    },
  };
}

export function createPlanFilter(planName: string): models.IBasicFilter {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table: 'Accounts',
      column: 'Plan Name',
    },
    operator: 'In',
    values: [planName],
    filterType: models.FilterType.Basic,
    requireSingleSelection: true,
    displaySettings: {
      isLockedInViewMode: true,
      displayName: 'Plan Name',
    },
  };
}

export function makePageMap(pages: { value: Page[] }): PageDisplayNameMap {
  return pages.value.reduce(displayNameToName, {});
}

export function displayNameToName(m: PageDisplayNameMap, p: Page): PageDisplayNameMap {
  return { ...m, [p.displayName]: p.name };
}
